import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldAlert, Loader2 } from "lucide-react";

const KIND_LABEL = {
  listing: "listing",
  event: "event",
  job: "job",
  need: "post",
  business: "business page",
};

/**
 * AdminRemoveButton — admin-only inline control that soft-removes a piece of content.
 * Only renders when the current user has role === "admin".
 *
 * On confirm: calls DELETE /api/admin/content/{type}/{id} and navigates the admin
 * to a safe landing page (list of same type) once the item is gone.
 */
export default function AdminRemoveButton({ type, id, title, redirectTo, onRemoved }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || user.role !== "admin") return null;
  if (!type || !id) return null;

  const label = KIND_LABEL[type] || "item";

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/content/${type}/${id}`);
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} removed`);
      setOpen(false);
      onRemoved?.();
      if (redirectTo) nav(redirectTo);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Could not remove this ${label}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="rounded-full border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
        data-testid={`admin-remove-${type}-${id}`}
        onClick={() => setOpen(true)}
      >
        <ShieldAlert className="w-4 h-4 mr-1" /> Admin: Remove
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-remove-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1E3A2F] flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" /> Remove this {label}?
            </DialogTitle>
            <DialogDescription>
              {title ? (
                <>You are about to take <span className="italic">"{title}"</span> off the site. It will be hidden from everyone immediately. This also auto-resolves any open reports on it.</>
              ) : (
                <>You are about to take this {label} off the site. It will be hidden from everyone immediately.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Textarea
              placeholder="Reason (optional, for your records)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[80px]"
              data-testid="admin-remove-reason"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="admin-remove-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={remove}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="admin-remove-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
              Remove {label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
